<template>
  <div
    class="form-canvas"
    @dragover.prevent
    @drop="onDrop"
  >
    <div v-if="fields.length === 0" class="empty-canvas">
      <div class="empty-icon">
        <i class="bx bx-plus-circle"></i>
      </div>
      <h5>Comece a construir seu formulário</h5>
      <p>Arraste campos da barra lateral ou clique em "+ Adicionar Campo"</p>
    </div>

    <draggable
      v-else
      v-model="localFields"
      :group="{ name: 'fields', pull: false, put: true }"
      handle=".drag-handle"
      animation="200"
      ghost-class="ghost-field"
      @end="onReorder"
    >
      <div
        v-for="field in localFields"
        :key="field.id || field._tempId"
        class="field-card"
        :class="{
          'selected': isSelected(field),
          'has-conditions': hasConditions(field)
        }"
        @click="selectField(field)"
      >
        <!-- Drag Handle & Label Row -->
        <div class="field-header">
          <div class="drag-handle">
            <i class="bx bx-grid-vertical"></i>
          </div>
          <div class="field-label-row">
            <!-- Ícone de cadeado para campos bloqueados (Nome/Email) -->
            <i v-if="isLockedField(field)" class="bx bx-lock-alt text-danger mr-1" title="Campo do sistema (não pode remover)"></i>
            <i v-if="isCompanionField(field)" class="bx bx-lock-alt text-info mr-1" title="Campo de acompanhante (não pode remover)"></i>
            <span class="field-label">{{ field.label || 'Sem label' }}</span>
            <span v-if="isStandard(field) && !isCompanionField(field)" class="badge badge-soft-primary" style="font-size: 10px;">Padrao</span>
            <span v-if="isCompanionField(field)" class="badge badge-info">Acompanhante</span>
            <span v-if="field.required" class="badge badge-required">Obrigatório</span>
            <span v-if="hasConditions(field)" class="badge badge-conditions">Tem condições</span>
          </div>
          <div class="field-actions">
            <!-- Duplicar: desabilitado para campos standard -->
            <button
              type="button"
              class="btn-action"
              :disabled="isStandard(field)"
              @click.stop="duplicateField(field)"
              :title="isStandard(field) ? 'Campos padrao nao podem ser duplicados' : 'Duplicar'"
            >
              <i class="bx bx-copy"></i>
            </button>
            <!-- Remover: desabilitado apenas para campos do sistema (name/email) -->
            <button
              type="button"
              class="btn-action btn-delete"
              :disabled="isLockedField(field)"
              @click.stop="removeField(field)"
              :title="isLockedField(field) ? 'Campos do sistema nao podem ser removidos' : 'Remover'"
            >
              <i class="bx bx-trash"></i>
            </button>
          </div>
        </div>

        <!-- Field Preview -->
        <div class="field-preview">
          <!-- Text Input Preview -->
          <template v-if="isTextType(field)">
            <input
              type="text"
              class="preview-input"
              :placeholder="field.placeholder || 'Enter your ' + field.label.toLowerCase() + '...'"
              disabled
            />
          </template>

          <!-- Textarea Preview -->
          <template v-else-if="field.fieldType && field.fieldType.name === 'textarea'">
            <textarea
              class="preview-textarea"
              :placeholder="field.placeholder || 'Enter text...'"
              rows="3"
              disabled
            ></textarea>
          </template>

          <!-- Select/Dropdown Preview -->
          <template v-else-if="isSelectType(field)">
            <div class="preview-select">
              <span>{{ field.placeholder || 'Select an option...' }}</span>
              <i class="bx bx-chevron-down"></i>
            </div>
            <!-- Show options if available -->
            <div v-if="field.options && field.options.length > 0" class="preview-options">
              <div
                v-for="(opt, idx) in field.options.slice(0, 4)"
                :key="idx"
                class="preview-option"
              >
                <span class="option-bullet" :style="{ backgroundColor: getOptionColor(idx) }"></span>
                {{ opt.label || opt }}
              </div>
              <div v-if="field.options.length > 4" class="preview-option text-muted">
                +{{ field.options.length - 4 }} mais opções
              </div>
            </div>
          </template>

          <!-- Radio Preview -->
          <template v-else-if="field.fieldType && field.fieldType.name === 'radio'">
            <div class="preview-radio-group">
              <div
                v-for="(opt, idx) in (field.options || []).slice(0, 4)"
                :key="idx"
                class="preview-radio"
              >
                <span class="radio-circle"></span>
                {{ opt.label || opt }}
              </div>
            </div>
          </template>

          <!-- Terms Acceptance Preview -->
          <template v-else-if="field.fieldType && field.fieldType.name === 'terms_acceptance'">
            <div class="preview-terms-acceptance">
              <div class="preview-terms-box">
                <div class="preview-terms-line" style="width: 80%"></div>
                <div class="preview-terms-line" style="width: 100%"></div>
                <div class="preview-terms-line" style="width: 65%"></div>
                <div class="preview-terms-line" style="width: 90%"></div>
                <div class="preview-terms-line" style="width: 45%"></div>
              </div>
              <div class="preview-terms-progress">
                <div class="preview-terms-progress-bar"></div>
              </div>
              <div class="preview-terms-accept">
                <span class="checkbox-box"></span>
                <span class="preview-terms-accept-label">{{ field.config && field.config.checkbox_label ? field.config.checkbox_label : 'Declaro que li e concordo...' }}</span>
              </div>
            </div>
          </template>

          <!-- Fake Ticket Preview -->
          <template v-else-if="field.fieldType && field.fieldType.name === 'fake_ticket'">
            <div class="preview-ticket">
              <div class="preview-ticket-body">
                <div class="preview-ticket-label">Ingresso</div>
                <div class="preview-ticket-title">{{ (field.config && field.config.ticket_title) || 'Acesso Completo' }}</div>
                <div class="preview-ticket-details">
                  <span>{{ (field.config && field.config.ticket_type) || 'Presencial' }}</span>
                  <span>·</span>
                  <span>{{ (field.config && field.config.ticket_validity) || '—' }}</span>
                </div>
              </div>
              <div class="preview-ticket-tear"></div>
              <div class="preview-ticket-price">
                <span class="preview-ticket-original">R$ {{ (field.config && field.config.ticket_value) || '450,00' }}</span>
                <span class="preview-ticket-free">R$ 0,00</span>
                <span class="preview-ticket-badge">Cortesia</span>
              </div>
            </div>
          </template>

          <!-- Disclaimer Preview -->
          <template v-else-if="field.fieldType && field.fieldType.name === 'disclaimer'">
            <div :class="['preview-disclaimer', (field.config && field.config.color) || 'amber']">
              <div class="preview-disclaimer-tag">Disclaimer</div>
              <div class="preview-disclaimer-header">
                <div class="preview-disclaimer-icon">
                  <i class="bx bx-info-circle"></i>
                </div>
                <span class="preview-disclaimer-title">{{ (field.config && field.config.title) || 'Informação importante' }}</span>
              </div>
              <div class="preview-disclaimer-body" v-if="field.config && field.config.body" v-html="field.config.body"></div>
              <div class="preview-disclaimer-steps" v-if="field.config && field.config.show_steps && field.config.steps && field.config.steps.length">
                <div class="preview-disclaimer-step" v-for="(step, si) in field.config.steps" :key="si">
                  <span class="preview-step-num">{{ si + 1 }}</span>
                  <span class="preview-step-text" v-html="step.text"></span>
                </div>
              </div>
            </div>
          </template>

          <!-- Instruções Preview -->
          <template v-else-if="field.fieldType && field.fieldType.name === 'instrucoes'">
            <!-- Modo card: banner e/ou aceite ativos -->
            <div
              v-if="field.config && (field.config.show_read_hint || field.config.require_acceptance)"
              class="preview-instrucoes-card"
              :class="'ins-color-' + (field.config.color || 'green')"
            >
              <div v-if="field.config.show_read_hint" class="preview-instrucoes-hint">
                <i class="bx bx-down-arrow-alt"></i>
                <span>{{ field.config.read_hint_text || 'Leia todas as instruções até o final para prosseguir.' }}</span>
              </div>
              <div
                class="preview-instrucoes"
                v-html="field.config.content || '<p>Texto de instruções...</p>'"
              ></div>
              <label v-if="field.config.require_acceptance" class="preview-instrucoes-accept">
                <input type="checkbox" disabled />
                <span>{{ field.config.acceptance_label || 'Li e estou ciente de todas as instruções acima.' }}</span>
              </label>
            </div>
            <!-- Modo simples: só texto -->
            <div
              v-else
              class="preview-instrucoes"
              v-html="(field.config && field.config.content) || '<p>Texto de instruções...</p>'"
            ></div>
          </template>

          <!-- Checkbox Preview -->
          <template v-else-if="field.fieldType && (field.fieldType.name === 'checkbox' || field.fieldType.name === 'terms')">
            <div class="preview-checkbox">
              <span class="checkbox-box"></span>
              <span>{{ field.options && field.options[0] ? field.options[0].label : 'Option' }}</span>
              <i v-if="field.config && field.config.link_url" class="bx bx-link-external" style="color: #556ee6; margin-left: 4px; font-size: 14px;" title="Contém link para os termos"></i>
            </div>
          </template>

          <!-- Date Preview -->
          <template v-else-if="isDateType(field)">
            <div class="preview-date">
              <i class="bx bx-calendar"></i>
              <span>{{ field.placeholder || 'Select date...' }}</span>
            </div>
          </template>

          <!-- File Upload Preview -->
          <template v-else-if="field.fieldType && (field.fieldType.name === 'file' || field.fieldType.name === 'image')">
            <div class="preview-file">
              <i class="bx bx-cloud-upload"></i>
              <span>Drag & drop or click to upload</span>
            </div>
          </template>

          <!-- Signature Preview -->
          <template v-else-if="field.fieldType && field.fieldType.name === 'signature'">
            <div class="preview-signature">
              <i class="bx bx-pen"></i>
              <span>Click to sign</span>
            </div>
          </template>

          <!-- Heading Preview -->
          <template v-else-if="field.fieldType && field.fieldType.name === 'heading'">
            <h4 class="preview-heading">{{ field.default_value || field.label }}</h4>
          </template>

          <!-- Divider Preview -->
          <template v-else-if="field.fieldType && field.fieldType.name === 'divider'">
            <hr class="preview-divider" />
          </template>

          <!-- Default Preview -->
          <template v-else>
            <input
              type="text"
              class="preview-input"
              :placeholder="field.placeholder || ''"
              disabled
            />
          </template>
        </div>

        <!-- Help Text -->
        <div v-if="field.help_text" class="field-help">
          {{ field.help_text }}
        </div>

        <!-- Conditional Fields Indicator -->
        <div v-if="hasConditions(field)" class="conditions-indicator">
          <i class="bx bx-git-branch"></i>
          <span>Exibir se: {{ getConditionSummary(field) }}</span>
        </div>
      </div>
    </draggable>
  </div>
</template>

<script>
import draggable from 'vuedraggable'

export default {
  name: 'FormCanvas',
  components: {
    draggable
  },
  props: {
    fields: {
      type: Array,
      default: () => []
    },
    selectedField: {
      type: Object,
      default: null
    }
  },
  data () {
    return {
      localFields: [],
      optionColors: ['#4285f4', '#34a853', '#fbbc04', '#ea4335', '#9c27b0', '#00bcd4']
    }
  },
  watch: {
    fields: {
      immediate: true,
      deep: true,
      handler (val) {
        this.localFields = [...val]
      }
    }
  },
  methods: {
    isSelected (field) {
      if (!this.selectedField) return false

      // Comparar por ID (apenas se ambos têm ID e não são null)
      if (this.selectedField.id && field.id && this.selectedField.id === field.id) {
        return true
      }

      // Comparar por _tempId (apenas se ambos têm _tempId e não são null)
      if (this.selectedField._tempId && field._tempId && this.selectedField._tempId === field._tempId) {
        return true
      }

      return false
    },
    isStandard (field) {
      // Campo é standard se:
      // 1. Tem a flag _isStandard (no frontend)
      // 2. Ou tem field_type.is_standard (do backend)
      // 3. Ou é um campo de acompanhante (não pode ser removido)
      return field._isStandard === true ||
             (field.field_type && field.field_type.is_standard === true) ||
             (field.fieldType && field.fieldType.is_standard === true) ||
             this.isCompanionField(field)
    },
    isLockedField (field) {
      // Apenas campos do sistema (name e email) são bloqueados para remoção.
      // Outros campos standard (phone, cpf, gender, badge_name, birth_date)
      // mostram badge "Padrão" mas podem ser removidos.
      var lockedNames = ['name', 'email']
      var fieldName = field.name || ''
      return (lockedNames.indexOf(fieldName) !== -1 && this.isStandard(field)) ||
             this.isCompanionField(field)
    },
    isCompanionField (field) {
      // Campo de acompanhante (auto-gerado)
      return (field.name || '').startsWith('companion_') || field.is_companion_field === true
    },
    isTextType (field) {
      const textTypes = ['text', 'email', 'phone', 'cpf', 'cnpj', 'cpf_cnpj', 'cep', 'rg', 'number', 'password', 'hidden']
      return field.fieldType && textTypes.includes(field.fieldType.name)
    },
    isSelectType (field) {
      const selectTypes = ['select', 'multiselect', 'state', 'country']
      return field.fieldType && selectTypes.includes(field.fieldType.name)
    },
    isDateType (field) {
      const dateTypes = ['date', 'time', 'datetime', 'daterange', 'birthdate']
      return field.fieldType && dateTypes.includes(field.fieldType.name)
    },
    hasConditions (field) {
      return field.conditions && field.conditions.length > 0
    },
    getConditionSummary (field) {
      if (!field.conditions || field.conditions.length === 0) return ''

      // Para campos de acompanhante, mostra a condição fixa
      if ((field.name || '').includes('companion_') &&
          !field.name.includes('has_companion')) {
        return 'Levarei acompanhante? = Sim'
      }

      const firstCondition = field.conditions[0]

      // Se tiver rules (formato novo)
      if (firstCondition.rules && firstCondition.rules.length > 0) {
        const firstRule = firstCondition.rules[0]
        if (firstRule.field_id && firstRule.value) {
          // Tenta encontrar o campo para pegar o label
          const referencedField = this.fields.find(f =>
            (f.id && String(f.id) === String(firstRule.field_id)) ||
            (f._tempId && String(f._tempId) === String(firstRule.field_id))
          )
          const fieldLabel = referencedField ? referencedField.label : 'Campo'
          return `${fieldLabel} = ${firstRule.value}`
        }
        return `${firstCondition.rules.length} condição(ões)`
      }

      // Formato antigo (field direto)
      if (firstCondition.field && firstCondition.value) {
        return `${firstCondition.field} = ${firstCondition.value}`
      }

      return 'Tem condições'
    },
    getOptionColor (index) {
      return this.optionColors[index % this.optionColors.length]
    },
    selectField (field) {
      this.$emit('select-field', field)
    },
    removeField (field) {
      this.$emit('remove-field', field.id || field._tempId)
    },
    duplicateField (field) {
      this.$emit('duplicate-field', field.id || field._tempId)
    },
    onReorder () {
      this.$emit('reorder-fields', this.localFields)
    },
    onDrop (event) {
      const data = event.dataTransfer.getData('application/json')
      if (data) {
        try {
          const fieldType = JSON.parse(data)
          this.$emit('add-field', fieldType)
        } catch (e) {
          console.error('Invalid drop data', e)
        }
      }
    }
  }
}
</script>

<style scoped>
.form-canvas {
  min-height: 400px;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  padding: 24px;
}

.empty-canvas {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 350px;
  text-align: center;
}

.empty-icon {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: #f0f4ff;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
}

.empty-icon i {
  font-size: 32px;
  color: #556ee6;
}

.empty-canvas h5 {
  font-size: 16px;
  font-weight: 600;
  color: #495057;
  margin-bottom: 8px;
}

.empty-canvas p {
  font-size: 14px;
  color: #6c757d;
  margin: 0;
}

/* Field Card */
.field-card {
  background: #fff;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  margin-bottom: 12px;
  padding: 16px;
  cursor: pointer;
  transition: all 0.15s ease;
}

.field-card:hover {
  border-color: #d0d5dd;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.field-card.selected {
  border-color: #556ee6;
  box-shadow: 0 0 0 3px rgba(85, 110, 230, 0.15);
}

.field-card.has-conditions {
  border-left: 3px solid #ffc107;
}

/* Field Header */
.field-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
}

.drag-handle {
  cursor: grab;
  color: #adb5bd;
  padding: 4px;
}

.drag-handle:hover {
  color: #556ee6;
}

.drag-handle:active {
  cursor: grabbing;
}

.field-label-row {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 8px;
}

.field-label {
  font-weight: 500;
  font-size: 14px;
  color: #344054;
}

.badge {
  font-size: 10px;
  font-weight: 500;
  padding: 2px 8px;
  border-radius: 4px;
}

.badge-required {
  background: #fee2e2;
  color: #dc2626;
}

.badge-conditions {
  background: #fef3c7;
  color: #d97706;
}

.field-actions {
  display: flex;
  gap: 4px;
  opacity: 0;
  transition: opacity 0.15s;
}

.field-card:hover .field-actions {
  opacity: 1;
}

.btn-action {
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: transparent;
  border-radius: 4px;
  color: #6c757d;
  cursor: pointer;
}

.btn-action:hover {
  background: #f3f4f6;
}

.btn-action.btn-delete:hover {
  background: #fee2e2;
  color: #dc2626;
}

/* Field Preview */
.field-preview {
  margin-bottom: 8px;
}

.preview-input {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  font-size: 14px;
  color: #9ca3af;
  background: #f9fafb;
}

.preview-textarea {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  font-size: 14px;
  color: #9ca3af;
  background: #f9fafb;
  resize: none;
}

.preview-select {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  font-size: 14px;
  color: #9ca3af;
  background: #f9fafb;
}

.preview-options {
  margin-top: 8px;
  padding-left: 8px;
}

.preview-option {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 0;
  font-size: 13px;
  color: #6b7280;
}

.option-bullet {
  width: 8px;
  height: 8px;
  border-radius: 50%;
}

.preview-radio-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.preview-radio {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #374151;
}

.radio-circle {
  width: 16px;
  height: 16px;
  border: 2px solid #d1d5db;
  border-radius: 50%;
}

.preview-checkbox {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #374151;
}

.checkbox-box {
  width: 16px;
  height: 16px;
  border: 2px solid #d1d5db;
  border-radius: 4px;
}

.preview-date {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  font-size: 14px;
  color: #9ca3af;
  background: #f9fafb;
}

.preview-file {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 24px;
  border: 2px dashed #e5e7eb;
  border-radius: 6px;
  background: #f9fafb;
  color: #9ca3af;
}

.preview-file i {
  font-size: 24px;
  margin-bottom: 8px;
}

.preview-signature {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 32px;
  border: 1px dashed #e5e7eb;
  border-radius: 6px;
  background: #f9fafb;
  color: #9ca3af;
}

.preview-heading {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #111827;
}

.preview-divider {
  border: none;
  border-top: 1px solid #e5e7eb;
  margin: 8px 0;
}

/* Help Text */
.field-help {
  font-size: 12px;
  color: #6b7280;
  margin-top: 4px;
}

/* Conditions Indicator */
.conditions-indicator {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 12px;
  padding: 8px 12px;
  background: #fffbeb;
  border-radius: 4px;
  font-size: 12px;
  color: #92400e;
}

.conditions-indicator i {
  font-size: 14px;
}

/* Terms Acceptance Preview */
.preview-terms-acceptance {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.preview-terms-box {
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.preview-terms-line {
  height: 8px;
  background: #e5e7eb;
  border-radius: 4px;
}

.preview-terms-progress {
  height: 3px;
  background: #e5e7eb;
  border-radius: 2px;
  overflow: hidden;
}

.preview-terms-progress-bar {
  height: 100%;
  width: 60%;
  background: linear-gradient(90deg, #1a7a5c, #24a87a);
  border-radius: 2px;
}

.preview-terms-accept {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  border: 1.5px solid #e5e7eb;
  border-radius: 8px;
  font-size: 12px;
  color: #6b7280;
}

.preview-terms-accept-label {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Fake Ticket Preview */
.preview-ticket {
  display: flex;
  align-items: stretch;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  overflow: hidden;
  position: relative;
  min-height: 72px;
}

.preview-ticket::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: linear-gradient(90deg, #556ee6, #7b8ff0, #556ee6);
}

.preview-ticket-body {
  flex: 1;
  padding: 12px 14px;
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.preview-ticket-label {
  font-size: 9px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #556ee6;
}

.preview-ticket-title {
  font-size: 12px;
  font-weight: 700;
  color: #1a1816;
  line-height: 1.3;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.preview-ticket-details {
  display: flex;
  gap: 6px;
  font-size: 10px;
  color: #9a958e;
}

.preview-ticket-tear {
  width: 1px;
  position: relative;
  flex-shrink: 0;
}

.preview-ticket-tear::before {
  content: '';
  position: absolute;
  top: 12px;
  bottom: 12px;
  left: 0;
  border-left: 1.5px dashed #e5e7eb;
}

.preview-ticket-price {
  width: 100px;
  flex-shrink: 0;
  padding: 10px 12px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  background: #fcfbfa;
}

.preview-ticket-original {
  font-size: 10px;
  color: #b8b2aa;
  text-decoration: line-through;
  text-decoration-color: #d4534b;
}

.preview-ticket-free {
  font-size: 16px;
  font-weight: 600;
  color: #556ee6;
  line-height: 1;
}

.preview-ticket-badge {
  font-size: 8px;
  font-weight: 600;
  text-transform: uppercase;
  padding: 2px 6px;
  border-radius: 8px;
  background: #f0faf5;
  border: 1px solid #c8ead9;
  color: #1a7a5c;
}

/* Disclaimer Preview */
.preview-disclaimer {
  border-radius: 10px;
  padding: 14px 16px;
  font-size: 11px;
}

.preview-disclaimer.amber {
  background: #fefaf0;
  border: 1px solid #f5e6b8;
}

.preview-disclaimer.info {
  background: #eef1fe;
  border: 1px solid #d4dcfb;
}

.preview-disclaimer-tag {
  font-size: 8px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.15em;
  margin-bottom: 4px;
  padding-left: 30px;
}

.preview-disclaimer.amber .preview-disclaimer-tag { color: #e8a317; }
.preview-disclaimer.info .preview-disclaimer-tag { color: #4f6ef7; }

.preview-disclaimer-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
}

.preview-disclaimer-icon {
  width: 22px;
  height: 22px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-size: 13px;
}

.preview-disclaimer.amber .preview-disclaimer-icon {
  background: rgba(232, 163, 23, 0.12);
  color: #e8a317;
}

.preview-disclaimer.info .preview-disclaimer-icon {
  background: rgba(79, 110, 247, 0.1);
  color: #4f6ef7;
}

.preview-disclaimer-title {
  font-size: 12px;
  font-weight: 700;
}

.preview-disclaimer.amber .preview-disclaimer-title { color: #7a5d0e; }
.preview-disclaimer.info .preview-disclaimer-title { color: #2d3a8c; }

.preview-disclaimer-body {
  font-size: 11px;
  line-height: 1.5;
  padding-left: 30px;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.preview-disclaimer.amber .preview-disclaimer-body { color: #9a7a2e; }
.preview-disclaimer.info .preview-disclaimer-body { color: #4a5694; }

.preview-disclaimer-steps {
  margin-top: 8px;
  padding-left: 30px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.preview-disclaimer-step {
  display: flex;
  align-items: flex-start;
  gap: 8px;
}

.preview-step-num {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 9px;
  font-weight: 700;
  flex-shrink: 0;
  background: #fff;
}

.preview-disclaimer.amber .preview-step-num { border: 1.5px solid #f5e6b8; color: #7a5d0e; }
.preview-disclaimer.info .preview-step-num { border: 1.5px solid #c4cef8; color: #2d3a8c; }

.preview-step-text {
  font-size: 10px;
  line-height: 1.4;
  padding-top: 2px;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
}

.preview-disclaimer.amber .preview-step-text { color: #9a7a2e; }
.preview-disclaimer.info .preview-step-text { color: #4a5694; }

/* Danger */
.preview-disclaimer.danger { background: #fef2f2; border-color: #fecaca; }
.preview-disclaimer.danger .preview-disclaimer-tag { color: #ef4444; }
.preview-disclaimer.danger .preview-disclaimer-icon { background: rgba(239, 68, 68, 0.1); color: #ef4444; }
.preview-disclaimer.danger .preview-disclaimer-title { color: #991b1b; }
.preview-disclaimer.danger .preview-disclaimer-body { color: #b91c1c; }
.preview-disclaimer.danger .preview-step-num { border-color: #fecaca; color: #991b1b; }
.preview-disclaimer.danger .preview-step-text { color: #b91c1c; }

/* Purple */
.preview-disclaimer.purple { background: #f5f3ff; border-color: #ddd6fe; }
.preview-disclaimer.purple .preview-disclaimer-tag { color: #8b5cf6; }
.preview-disclaimer.purple .preview-disclaimer-icon { background: rgba(139, 92, 246, 0.1); color: #8b5cf6; }
.preview-disclaimer.purple .preview-disclaimer-title { color: #5b21b6; }
.preview-disclaimer.purple .preview-disclaimer-body { color: #6d28d9; }
.preview-disclaimer.purple .preview-step-num { border-color: #ddd6fe; color: #5b21b6; }
.preview-disclaimer.purple .preview-step-text { color: #6d28d9; }

/* Neutral */
.preview-disclaimer.neutral { background: #f9fafb; border-color: #e5e7eb; }
.preview-disclaimer.neutral .preview-disclaimer-tag { color: #6b7280; }
.preview-disclaimer.neutral .preview-disclaimer-icon { background: rgba(107, 114, 128, 0.1); color: #6b7280; }
.preview-disclaimer.neutral .preview-disclaimer-title { color: #374151; }
.preview-disclaimer.neutral .preview-disclaimer-body { color: #4b5563; }
.preview-disclaimer.neutral .preview-step-num { border-color: #e5e7eb; color: #374151; }
.preview-disclaimer.neutral .preview-step-text { color: #4b5563; }

/* Instruções Preview */
.preview-instrucoes {
  width: 100%;
  font-size: 14px;
  line-height: 1.6;
  color: #374151;
}
.preview-instrucoes p { margin: 0 0 0.5rem; }
.preview-instrucoes p:last-child { margin-bottom: 0; }
.preview-instrucoes strong,
.preview-instrucoes b { font-weight: 700; }
.preview-instrucoes em,
.preview-instrucoes i { font-style: italic; }
.preview-instrucoes h2 { font-size: 1.3em; font-weight: 700; margin: 0.6rem 0 0.4rem; }
.preview-instrucoes h3 { font-size: 1.1em; font-weight: 600; margin: 0.5rem 0 0.3rem; }
.preview-instrucoes ul { list-style: disc outside; padding-left: 1.5rem; margin: 0.5rem 0; }
.preview-instrucoes ol { list-style: decimal outside; padding-left: 1.5rem; margin: 0.5rem 0; }
.preview-instrucoes li { margin-bottom: 0.25rem; }
.preview-instrucoes a { color: #0ea5e9; text-decoration: underline; }
.preview-instrucoes .text-tiny { font-size: 0.7em; }
.preview-instrucoes .text-small { font-size: 0.85em; }
.preview-instrucoes .text-big { font-size: 1.4em; }
.preview-instrucoes .text-huge { font-size: 1.8em; }
/* Card único — espelha o componente de Termos, com cor temática */
.preview-instrucoes-card {
  --ins-border: #cfe6da;
  --ins-hint-bg: #f0faf5;
  --ins-hint-border: #c2e8d5;
  --ins-hint-text: #1a7a5c;
  width: 100%;
  background: #fff;
  border: 1px solid var(--ins-border);
  border-radius: 12px;
  overflow: hidden;
}
.preview-instrucoes-card.ins-color-amber {
  --ins-border: #f0e4bf; --ins-hint-bg: #fef8ec; --ins-hint-border: #f0ddb0; --ins-hint-text: #8a6d2b;
}
.preview-instrucoes-card.ins-color-info {
  --ins-border: #d4dcfb; --ins-hint-bg: #eef2ff; --ins-hint-border: #d4dcfb; --ins-hint-text: #3b4fa0;
}
.preview-instrucoes-card.ins-color-danger {
  --ins-border: #fbcaca; --ins-hint-bg: #fef2f2; --ins-hint-border: #fbcaca; --ins-hint-text: #b3261e;
}
.preview-instrucoes-card.ins-color-purple {
  --ins-border: #ddd6fe; --ins-hint-bg: #f5f3ff; --ins-hint-border: #ddd6fe; --ins-hint-text: #6d28d9;
}
.preview-instrucoes-card.ins-color-neutral {
  --ins-border: #e5e7eb; --ins-hint-bg: #f9fafb; --ins-hint-border: #e5e7eb; --ins-hint-text: #4b5563;
}
.preview-instrucoes-card .preview-instrucoes {
  padding: 14px 16px;
}
.preview-instrucoes-hint {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 9px 16px;
  background: var(--ins-hint-bg);
  border-bottom: 1px solid var(--ins-hint-border);
  font-size: 13px;
  color: var(--ins-hint-text);
}
.preview-instrucoes-hint i { font-size: 16px; }
.preview-instrucoes-accept {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px 16px;
  border-top: 1px solid var(--ins-border);
  background: #fdfcfb;
  font-size: 13px;
  color: #3d3833;
}
.preview-instrucoes-accept input { margin-top: 2px; }

/* Ghost */
.ghost-field {
  opacity: 0.5;
  background: #f3f4f6;
}
</style>
